import React, { useState, useEffect } from 'react';

/**
 * CandlestickConditionBuilder - UI for selecting candlestick patterns
 * 
 * @param {Object} props
 * @param {Object} props.condition - Current condition data
 * @param {Function} props.onChange - Change handler (updatedCondition) => void
 * @param {Object} props.rulesSchema - Rules schema from API containing candlestickPatterns
 * @param {boolean} props.disabled - Whether inputs are disabled
 */
const CandlestickConditionBuilder = ({
    condition,
    onChange,
    rulesSchema = null,
    disabled = false,
}) => {
    // Extract patterns from schema
    const candlestickPatterns = rulesSchema?.candlestickPatterns || [];

    // Group patterns by category
    const categories = [...new Set(candlestickPatterns.map(p => p.category))];

    const handlePatternChange = (patternName) => {
        onChange({
            ...condition,
            patternType: patternName,
        });
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="label">
                    <span className="label-text font-medium">Select Candlestick Pattern</span>
                    <span className="label-text-alt text-base-content/50">Phase 2 Patterns</span>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    {categories.map(category => (
                        <div key={category} className="space-y-2">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-base-content/40 px-1">
                                {category}
                            </h4>
                            <div className="flex flex-col gap-2">
                                {candlestickPatterns
                                    .filter(p => p.category === category)
                                    .map(pattern => (
                                        <div
                                            key={pattern.name}
                                            className={`
                        flex flex-col p-3 rounded-lg border-2 cursor-pointer transition-all
                        ${condition.patternType === pattern.name
                                                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                                    : 'border-base-300 hover:border-base-content/20 bg-base-100'}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                                            onClick={() => !disabled && handlePatternChange(pattern.name)}
                                        >
                                            <div className="flex items-center justify-between pointer-events-none">
                                                <span className="font-bold text-sm">{pattern.displayName}</span>
                                                {condition.patternType === pattern.name && (
                                                    <span className="badge badge-primary badge-sm">Selected</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-base-content/60 mt-1 pointer-events-none">
                                                {pattern.description}
                                            </p>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {!candlestickPatterns.length && (
                <div className="alert alert-warning">
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    <span>No candlestick patterns available in the schema. Please check if your backend supports Phase 2 rules.</span>
                </div>
            )}
        </div>
    );
};

export default CandlestickConditionBuilder;
